import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";

export interface PageTabItem {
  key: string;
  label: string;
  icon?: string;
  badge?: React.ReactNode;
  disabled?: boolean;
}

interface PageTabsState {
  tabs: PageTabItem[];
  activeKey: string;
  onSelect: (key: string) => void;
  rightActions?: React.ReactNode;
}

const emptyState: PageTabsState = {
  tabs: [],
  activeKey: "",
  onSelect: () => {},
  rightActions: undefined,
};

// Two contexts on purpose: pages subscribe only to the stable setter so
// state updates don't re-render the caller (which would create an update
// loop, since pages pass inline literals into useRegisterPageTabs).
const PageTabsStateContext = createContext<PageTabsState>(emptyState);
const PageTabsSetContext = createContext<
  (state: PageTabsState | null) => void
>(() => {});

function tabsEqual(a: PageTabItem[], b: PageTabItem[]) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (
      x.key !== y.key ||
      x.label !== y.label ||
      x.icon !== y.icon ||
      x.badge !== y.badge ||
      x.disabled !== y.disabled
    ) {
      return false;
    }
  }
  return true;
}

function stateEqual(a: PageTabsState, b: PageTabsState) {
  return (
    a.activeKey === b.activeKey &&
    a.onSelect === b.onSelect &&
    a.rightActions === b.rightActions &&
    tabsEqual(a.tabs, b.tabs)
  );
}

export const PageTabsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<PageTabsState>(emptyState);

  const setPageTabs = useCallback((next: PageTabsState | null) => {
    setState((prev) => {
      const value = next ?? emptyState;
      return stateEqual(prev, value) ? prev : value;
    });
  }, []);

  return (
    <PageTabsSetContext.Provider value={setPageTabs}>
      <PageTabsStateContext.Provider value={state}>
        {children}
      </PageTabsStateContext.Provider>
    </PageTabsSetContext.Provider>
  );
};

// Layout-side hook: subscribes to state to render the bar.
export const usePageTabsState = () => useContext(PageTabsStateContext);

/**
 * Register a set of tabs to be rendered by the layout, in a sticky bar
 * directly under the navbar. Pages call this via <PageTabs .../>.
 *
 * We only subscribe to the stable setter context, so provider state
 * updates never re-render the caller. The equality check inside the
 * setter means calling it every render is a no-op when nothing changed.
 */
export function useRegisterPageTabs(state: PageTabsState) {
  const setPageTabs = useContext(PageTabsSetContext);
  const latest = useRef(state);
  latest.current = state;

  useEffect(() => {
    setPageTabs(latest.current);
  });

  useEffect(
    () => () => {
      setPageTabs(null);
    },
    [setPageTabs]
  );
}
